import { ICartViewModel, ItemAnalyticsModel, ItemListAnalyticsModel } from '@/@types/client-api';
import { AnalyticsEventName } from './constants';
import { datadogRum } from '@/lib/DatadogService';
import { delay } from '@/utils/promises';
import Cookies from 'js-cookie';
import { UAParser } from 'ua-parser-js';

const itemCategoryKeys = [
  'item_category',
  'item_category2',
  'item_category3',
  'item_category4',
  'item_category5'
] as const;

type ItemCategoryKey = (typeof itemCategoryKeys)[number];

type ItemCategoryParams = Partial<Record<ItemCategoryKey, string>>;

export type ItemAnalyticsParams = ItemCategoryParams & {
  item_id?: string | null;
  item_name?: string | null;
  affiliation?: string | null;
  coupon?: string | null;
  index: number;
  item_brand?: string | null;
  price: number;
  location_id?: string | null;
  parentId?: string | null;
  parentName?: string | null;
  discount: number;
  item_list_price?: number | null;
  item_list_id?: string | null;
  item_list_name?: string | null;
};

type PLIPromoCookie = {
  sourcecode?: string;
  keycode?: string;
};

const readCouponFromPLIPromoCookie = (): string => {
  const promoCookie = Cookies.get('PLIPromo');
  if (!promoCookie) return '';

  try {
    const promo = JSON.parse(promoCookie) as PLIPromoCookie;
    const sourceCode = promo.sourcecode?.trim();
    const keyCode = promo.keycode?.trim();

    return sourceCode?.length === 4 && keyCode?.length === 5 ? `${sourceCode} ${keyCode}` : '';
  } catch {
    return '';
  }
};

const resolveItemCoupon = (coupon?: string | null): string => {
  return coupon?.trim() || readCouponFromPLIPromoCookie();
};

export const mapCategories = (categories: string[]): ItemCategoryParams => {
  return categories.reduce((acc, category, index) => {
    const categoryKey = itemCategoryKeys[index];

    if (categoryKey) {
      acc[categoryKey] = category;
    }

    return acc;
  }, {} as ItemCategoryParams);
};

export const ConvertItemAnalyticsModelBase = (
  itemAnalyticsModel?: ItemAnalyticsModel,
  itemListAnalyticsModel?: ItemListAnalyticsModel,
  itemIndex?: number
): ItemAnalyticsParams | undefined => {
  if (!itemAnalyticsModel) return undefined;

  const normalizedIndex =
    typeof itemIndex === 'number' && itemIndex >= 0 ? Math.trunc(itemIndex) + 1 : 1;

  const price = itemAnalyticsModel?.price ?? 0;
  const listPrice = itemAnalyticsModel?.listPrice ?? 0;
  const explicitDiscount = itemAnalyticsModel?.discount ?? 0;
  const computedDiscount = Math.max(listPrice - price, 0);
  const discount = explicitDiscount > 0 ? explicitDiscount : computedDiscount;

  return {
    item_id: itemAnalyticsModel?.id,
    item_name: itemAnalyticsModel?.name,
    affiliation: itemAnalyticsModel?.affiliation,
    coupon: resolveItemCoupon(itemAnalyticsModel?.coupon),
    index: normalizedIndex,
    item_brand: itemAnalyticsModel?.brand,
    price,
    location_id: itemAnalyticsModel?.location,
    ...mapCategories(itemAnalyticsModel?.categories || []),
    parentId: itemAnalyticsModel?.parentId,
    parentName: itemAnalyticsModel?.parentName,
    discount,
    item_list_price: itemAnalyticsModel.listPrice,
    item_list_id: itemListAnalyticsModel?.listId,
    item_list_name: itemListAnalyticsModel?.listName
  };
};

export const ConvertCartToItemAnalyticsModels = (cart?: ICartViewModel) => {
  return (
    cart?.orderLines
      ?.filter(x => x !== undefined)
      .map((x, index) =>
        ConvertItemAnalyticsModelBase(x.itemAnalyticsModel, x.itemListAnalyticsModel, index)
      ) ?? []
  );
};

export const ConvertItemAnalyticsModel = (
  itemAnalyticsModel?: ItemAnalyticsModel,
  quantity?: number,
  itemListAnalyticsModel?: ItemListAnalyticsModel,
  itemIndex?: number
) => {
  return {
    currency: itemAnalyticsModel?.currency || undefined,
    value: itemAnalyticsModel?.price || 0,
    items: [
      quantity
        ? {
            ...ConvertItemAnalyticsModelBase(itemAnalyticsModel, itemListAnalyticsModel, itemIndex),
            quantity
          }
        : ConvertItemAnalyticsModelBase(itemAnalyticsModel, itemListAnalyticsModel, itemIndex)
    ]
  };
};

export const getUID = () =>
  String(Date.now().toString(32) + Math.random().toString(16)).replace(/\./g, '');

export const createTransactionId = (transactionPrefix: string) => transactionPrefix + getUID();

const getFirstVisit = () => {
  const existingCookie = Cookies.get('_pli_first_visit');
  if (existingCookie) {
    try {
      return JSON.parse(existingCookie);
    } catch {
      // malformed cookie: fall through and recreate it
    }
  }

  const firstVisit = {
    date: new Date().toISOString().split('T')[0],
    timestamp: new Date().getTime()
  };
  Cookies.set('_pli_first_visit', JSON.stringify(firstVisit), { expires: 730 }); // 2 years
  return firstVisit;
};

const resolveTrafficSource = () => {
  const params = new URLSearchParams(window.location.search);
  const utmSource = params.get('utm_source');
  const utmMedium = params.get('utm_medium');
  const utmCampaign = params.get('utm_campaign');

  if (utmSource || utmMedium || utmCampaign) {
    return {
      source: utmSource || '',
      medium: utmMedium || '',
      campaign: utmCampaign || ''
    };
  }

  if (document.referrer) {
    try {
      const referrerHostname = new URL(document.referrer).hostname;
      if (referrerHostname !== window.location.hostname) {
        return {
          source: referrerHostname,
          medium: 'referral',
          campaign: ''
        };
      }
    } catch {
      return {
        source: '(direct)',
        medium: '(none)',
        campaign: ''
      };
    }
  }

  return {
    source: '(direct)',
    medium: '(none)',
    campaign: ''
  };
};

const TRAFFIC_SOURCE_STORAGE_KEY = '_pli_traffic_source';

// Session-scoped landing attribution: resolved on the first call of the session and
// persisted, so a mid-session re-send never misattributes to '(direct)' after the
// UTM params are gone from the URL.
const getTrafficSource = () => {
  try {
    const stored = sessionStorage.getItem(TRAFFIC_SOURCE_STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {
    // ignore and resolve again
  }

  const trafficSource = resolveTrafficSource();

  try {
    sessionStorage.setItem(TRAFFIC_SOURCE_STORAGE_KEY, JSON.stringify(trafficSource));
  } catch {
    // sessionStorage unavailable: still return the resolved value
  }

  return trafficSource;
};

const getGaStreamId = () =>
  String(import.meta.env.VITE_APP_GA4_MEASUREMENT_ID || '').replace(/^G-/, '');

// GTM's first-party cookie copies can contain the literal string 'deleted'; treat it as absent
const readGaCookie = (name: string) => {
  const value = Cookies.get(name);
  return value && value !== 'deleted' ? value : undefined;
};

/**
 * Client id lives only in the `_ga` (or `_gtm_ga`) cookie: `GA1.1.123456789.1699999999`.
 * GA4's client_id is the LAST TWO segments joined: `123456789.1699999999`.
 */
export const parseGaClientId = (cookieValue: string | undefined): string | null => {
  if (!cookieValue) return null;

  const parts = cookieValue.split('.');
  if (!parts[0]?.startsWith('GA') || parts.length < 4) return null;

  return parts.slice(-2).join('.');
};

/**
 * Session cookie `_ga_<streamId>` holds the session id (an epoch timestamp), not the client id.
 * GS1: `GS1.1.<sessionId>.<sessionNumber>.<engaged>...`
 * GS2: `GS2.1.s<sessionId>$o<sessionNumber>$g...`
 */
export const parseGaSessionCookie = (cookieValue: string | undefined) => {
  if (!cookieValue) {
    return { sessionId: null, sessionNumber: null };
  }

  const parts = cookieValue.split('.');

  if (parts[0] === 'GS2' && parts.length >= 3) {
    const dataString = parts.slice(2).join('.');

    return {
      sessionId: dataString.match(/^s(\d+)/)?.[1] || null,
      sessionNumber: parseInt(dataString.match(/\$o(\d+)/)?.[1] ?? '', 10) || null
    };
  }

  if (parts[0] === 'GS1' && parts.length >= 4) {
    return {
      sessionId: parts[2] || null,
      sessionNumber: parseInt(parts[3], 10) || null
    };
  }

  return { sessionId: null, sessionNumber: null };
};

/** Raw GA session cookie value, preferring the first-party `_gtm_ga*` copies. */
export const getRawGaSessionCookie = () => {
  const streamId = getGaStreamId();
  return (
    readGaCookie(`_gtm_ga_${streamId}`) ||
    readGaCookie('_gtm_ga') ||
    readGaCookie(`_ga_${streamId}`) ||
    readGaCookie('_ga')
  );
};

/** GA identifiers, joinable 1:1 with the GA4 BigQuery export. */
export const getGaIds = () => {
  const streamId = getGaStreamId();
  const clientCookie = readGaCookie('_gtm_ga') || readGaCookie('_ga');
  const sessionCookie = readGaCookie(`_gtm_ga_${streamId}`) || readGaCookie(`_ga_${streamId}`);
  const { sessionId, sessionNumber } = parseGaSessionCookie(sessionCookie);

  return {
    clientId: parseGaClientId(clientCookie),
    sessionId,
    sessionNumber
  };
};

type NetworkInformationLike = {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
};

export const createSessionContextEvent = () => {
  const parser = new UAParser(navigator.userAgent);
  const result = parser.getResult();
  const trafficSource = getTrafficSource();
  const firstVisit = getFirstVisit();
  const { clientId, sessionId, sessionNumber } = getGaIds();
  const connection = (navigator as Navigator & { connection?: NetworkInformationLike }).connection;
  const deviceMemory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;

  return {
    event: AnalyticsEventName.SessionContext,
    client_id: clientId || 'unknown',
    ga_session_id: sessionId || 'unknown',
    ga_session_number: sessionNumber?.toString() || '1',
    platform: result.os.name ? `${result.os.name} ${result.os.version || ''}`.trim() : 'unknown',
    os: result.os.name || 'unknown',
    os_version: result.os.version || 'unknown',
    browser: result.browser.name || 'unknown',
    browser_version: result.browser.version || 'unknown',
    device_category: result.device.type || 'desktop',
    device_vendor: result.device.vendor || 'unknown',
    device_model: result.device.model || 'unknown',
    user_agent: navigator.userAgent,
    language: navigator.language,
    languages: navigator.languages?.join(',') || navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timezone_offset_min: new Date().getTimezoneOffset(),
    screen_resolution: `${window.screen.width}x${window.screen.height}`,
    viewport_size: `${window.innerWidth}x${window.innerHeight}`,
    pixel_ratio: window.devicePixelRatio,
    color_depth: window.screen.colorDepth,
    device_memory_gb: deviceMemory ?? 0,
    cpu_cores: navigator.hardwareConcurrency || 0,
    touch_support: navigator.maxTouchPoints > 0,
    connection_type: connection?.effectiveType || 'unknown',
    connection_downlink_mbps: connection?.downlink ?? 0,
    connection_rtt_ms: connection?.rtt ?? 0,
    connection_save_data: connection?.saveData ?? false,
    cookies_enabled: navigator.cookieEnabled,
    page_hostname: window.location.hostname,
    landing_page: window.location.pathname + window.location.search,
    traffic_source: trafficSource,
    first_visit_date: firstVisit.date,
    first_visit_timestamp: firstVisit.timestamp.toString(),
    timestamp: new Date().getTime(),
    dataSource: 'FrontEnd'
  };
};

// Titles defined across multiple lines in markup keep the raw newlines/indentation;
// collapse them so page_title is always a single-line value
export const getNormalizedPageTitle = () => document.title.replace(/\s+/g, ' ').trim();

let lastEventTimestamp = Date.now();
let pageViewCount = 0;
let currentPageReferrer: string | null = null;

// For SPA navigations the browser referrer never changes; track the previous virtual
// URL so every event carries the referrer of the page it actually fired on.
const getCurrentPageReferrer = () => currentPageReferrer ?? document.referrer;

// GA4 semantics: engagement_time_msec is the time elapsed since the previous event.
const markEngagementTick = () => {
  const now = Date.now();
  const elapsed = Math.max(0, now - lastEventTimestamp);
  lastEventTimestamp = now;
  return elapsed;
};

const getCampaignParams = (queryString: string) => {
  const params = new URLSearchParams(queryString);
  const campaign: Record<string, string> = {};

  params.forEach((value, key) => {
    if (key.startsWith('utm_') || key === 'gclid' || key === 'gbraid' || key === 'wbraid') {
      campaign[key] = value;
    }
  });

  return campaign;
};

// _ns is a transient navigation flag the search page strips from the address bar right
// after navigating; drop it from reported URLs so page_location matches what the user sees.
const TRANSIENT_QUERY_PARAMS = ['_ns'];

export const stripTransientQueryParams = (queryString: string) => {
  if (!queryString) return '';

  const params = new URLSearchParams(queryString);
  TRANSIENT_QUERY_PARAMS.forEach(key => params.delete(key));
  const normalized = params.toString();

  return normalized ? `?${normalized}` : '';
};

export type SyntheticPageViewParams = {
  uri: string;
  refUri: string;
  queryString: string;
  navigationType: 'spa' | 'pageLoad';
  operationId: string;
  renderTimeMs: number;
  pageLoadTimeMs?: number;
};

export const createSyntheticPageViewEvent = (params: SyntheticPageViewParams) => {
  const pageReferrer =
    params.navigationType === 'spa' && params.refUri ? params.refUri : document.referrer;
  const navTiming = performance.getEntriesByType('navigation')[0] as
    PerformanceNavigationTiming | undefined;

  pageViewCount += 1;
  currentPageReferrer = pageReferrer;

  return {
    is_synthetic: true,
    page_location: `${params.uri}${stripTransientQueryParams(params.queryString)}`,
    page_path: new URL(params.uri).pathname,
    page_hostname: window.location.hostname,
    page_title: getNormalizedPageTitle(),
    page_referrer: pageReferrer,
    navigation_type: params.navigationType,
    ...(params.navigationType === 'pageLoad' && navTiming
      ? { browser_navigation_type: navTiming.type }
      : {}),
    page_view_number: pageViewCount,
    operation_id: params.operationId,
    // Included in the payload (not added by syntheticEventHubEvent) because the pageview
    // reaches eventHubDataLayer via the loadExternals.js dataLayer mirror, not a direct push
    engagement_time_msec: markEngagementTick(),
    render_time_msec: Math.round(params.renderTimeMs),
    ...(params.pageLoadTimeMs && params.pageLoadTimeMs > 0
      ? {
          page_load_time_msec: Math.round(params.pageLoadTimeMs),
          ...(navTiming
            ? {
                ttfb_msec: Math.round(navTiming.responseStart),
                dom_content_loaded_msec: Math.round(navTiming.domContentLoadedEventEnd)
              }
            : {})
        }
      : {}),
    viewport_size: `${window.innerWidth}x${window.innerHeight}`,
    ...getCampaignParams(params.queryString)
  };
};

const eventListenersStack = new Array<() => void>();

export const PushAnalyticsEvent = (event: AnalyticsEventName, eventData: object) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null, items: null });

  const eventCallback = eventListenersStack.pop();
  if (eventCallback) Object.assign(eventData, { eventCallback });

  window.dataLayer.push({
    event: event,
    ...eventData
  });
};

/**
 * Wait for events to be tracked before performing actions (eg: redirect).
 *
 * ‼ Beware of using this too much, as it can create too many timeouts depending on the type of event
 * */
export const waitForEvent = async (event: () => unknown, timeout = 2000) => {
  const abort = new AbortController();

  try {
    return await Promise.race([
      new Promise<void>(resolve => {
        eventListenersStack.push(resolve);
        event();
      }),
      // timeout fallback
      delay(timeout, abort.signal)
    ]);
  } finally {
    abort.abort();
  }
};

const logError = (errorMessage: string) => {
  datadogRum.addError(new Error(errorMessage), {
    customMessage: errorMessage
  });
};

export const syntheticEventHubEvent = async (event: object) => {
  try {
    window.eventHubDataLayer = window.eventHubDataLayer || [];

    // Page context is captured at push time so each event keeps the page it fired on,
    // even when the user navigates away before the queue is flushed. Caller-provided
    // fields win (e.g. virtual_page_view supplies richer values).
    const newEvent = {
      page_location: window.location.href,
      page_path: window.location.pathname,
      page_hostname: window.location.hostname,
      page_title: getNormalizedPageTitle(),
      page_referrer: getCurrentPageReferrer(),
      engagement_time_msec: markEngagementTick(),
      ...event,
      analytics_event_timestamp: new Date().getTime(),
      'gtm.uniqueEventId': getUID() // synthetic gtm.uniqueEventId
    };

    window.eventHubDataLayer.push(newEvent);
  } catch (error) {
    logError('synthetic hub event error');
  }
};
